import { axiosInstance } from '../App'
import { SECURED_API_BASE } from './apiConfig'
import {
  ContractHonorariumCombinationDTO,
  ContractHonorariumCreateDTO,
  ContractHonorariumCreateResultDTO,
  ContractHonorariumDetailDTO,
  ContractHonorariumHistoryItemDTO,
  ContractHonorariumListItemDTO,
  ContractHonorariumListQueryParams,
  ContractHonorariumOptionDTO,
  ContractHonorariumOptionsDTO,
  ContractHonorariumPlanOptionDTO,
  ContractHonorariumStatus,
  ContractHonorariumUpdateDTO,
  ContractHonorariumBulkCloseDTO,
  ContractHonorariumBulkEditDTO,
  ContractHonorariumBulkResultDTO,
  ContractHonorariumSkipReason,
} from '../dto/contractHonorarium/ContractHonorariumDTO'

const BASE = `${SECURED_API_BASE}/contract-honorariums`

type UnknownRecord = Record<string, unknown>

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === 'object' && value !== null
}

function toNumberOrNull(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null
  const parsed = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

function toText(value: unknown): string {
  return typeof value === 'string' ? value : ''
}

function toTextOrNull(value: unknown): string | null {
  return typeof value === 'string' ? value : null
}

function toIsoDate(value: unknown): string | null {
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 10)
  if (Array.isArray(value) && value.length >= 3) {
    const [year, month, day] = value.map(Number)
    if ([year, month, day].every(Number.isFinite)) {
      return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
    }
  }
  return null
}

function toIsoDateTime(value: unknown): string | null {
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(value)) return value.slice(0, 19)
  if (Array.isArray(value) && value.length >= 5) {
    const [year, month, day, hour, minute] = value.map(Number)
    const pad = (part: number) => String(part).padStart(2, '0')
    if ([year, month, day, hour, minute].every(Number.isFinite)) {
      return `${year}-${pad(month)}-${pad(day)}T${pad(hour)}:${pad(minute)}`
    }
  }
  return null
}

const STATUSES: ContractHonorariumStatus[] = ['CURRENT', 'FUTURE', 'EXPIRED']

function toStatus(value: unknown): ContractHonorariumStatus {
  return STATUSES.find((status) => status === value) ?? 'EXPIRED'
}

function normalizeListItem(raw: unknown): ContractHonorariumListItemDTO | null {
  if (!isRecord(raw)) return null
  const id = toNumberOrNull(raw.contracthonorariumid)
  const dateStart = toIsoDate(raw.datestart)
  if (id === null || dateStart === null) return null

  return {
    contracthonorariumid: id,
    professional: toText(raw.professional),
    billingentityname: toText(raw.billingentityname),
    healthentityname: toText(raw.healthentityname),
    healthentityplanname: toText(raw.healthentityplanname),
    nomenclator: toText(raw.nomenclator),
    buildingname: toText(raw.buildingname),
    distributioncontracttype: toNumberOrNull(raw.distributioncontracttype),
    distributiontypename: toText(raw.distributiontypename),
    honopercentage: toNumberOrNull(raw.honopercentage),
    copaymentpercentage: toNumberOrNull(raw.copaymentpercentage),
    diferentialpercentage: toNumberOrNull(raw.diferentialpercentage),
    fixedamount: toNumberOrNull(raw.fixedamount),
    datestart: dateStart,
    dateend: toIsoDate(raw.dateend),
    status: toStatus(raw.status),
    billingEntityProfessional: raw.billingEntityProfessional === true,
  }
}

function normalizeDetail(raw: unknown): ContractHonorariumDetailDTO | null {
  if (!isRecord(raw)) return null
  const id = toNumberOrNull(raw.id)
  const validFrom = toIsoDate(raw.validFrom)
  if (id === null || validFrom === null) return null

  return {
    id,
    billingEntityName: toTextOrNull(raw.billingEntityName),
    healthEntityName: toTextOrNull(raw.healthEntityName),
    healthEntityPlanName: toTextOrNull(raw.healthEntityPlanName),
    professionalName: toTextOrNull(raw.professionalName),
    nomenclatorName: toTextOrNull(raw.nomenclatorName),
    buildingName: toTextOrNull(raw.buildingName),
    distributionTypeId: toNumberOrNull(raw.distributionTypeId),
    honoPercentage: toNumberOrNull(raw.honoPercentage),
    copaymentPercentage: toNumberOrNull(raw.copaymentPercentage),
    diferentialPercentage: toNumberOrNull(raw.diferentialPercentage),
    fixedAmount: toNumberOrNull(raw.fixedAmount),
    validFrom,
    validUntil: toIsoDate(raw.validUntil),
    status: toStatus(raw.status),
    billingEntityProfessional: raw.billingEntityProfessional === true,
  }
}

function normalizeHistoryItem(raw: unknown): ContractHonorariumHistoryItemDTO | null {
  if (!isRecord(raw)) return null
  const id = toNumberOrNull(raw.id)
  const validFrom = toIsoDate(raw.validFrom)
  if (id === null || validFrom === null) return null

  return {
    id,
    validFrom,
    validUntil: toIsoDate(raw.validUntil),
    billingEntityName: toTextOrNull(raw.billingEntityName),
    distributionTypeId: toNumberOrNull(raw.distributionTypeId),
    distributionTypeName: toText(raw.distributionTypeName),
    honoPercentage: toNumberOrNull(raw.honoPercentage),
    copaymentPercentage: toNumberOrNull(raw.copaymentPercentage),
    diferentialPercentage: toNumberOrNull(raw.diferentialPercentage),
    fixedAmount: toNumberOrNull(raw.fixedAmount),
    changeDate: toIsoDateTime(raw.changeDate),
    userName: toText(raw.userName),
  }
}

function normalizeCombination(raw: unknown): ContractHonorariumCombinationDTO | null {
  if (!isRecord(raw)) return null
  return {
    billingEntityName: toTextOrNull(raw.billingEntityName),
    healthEntityName: toTextOrNull(raw.healthEntityName),
    healthEntityPlanName: toTextOrNull(raw.healthEntityPlanName),
    professionalName: toTextOrNull(raw.professionalName),
    nomenclatorName: toTextOrNull(raw.nomenclatorName),
    buildingName: toTextOrNull(raw.buildingName),
  }
}

function normalizeOption(raw: unknown): ContractHonorariumOptionDTO | null {
  if (!isRecord(raw)) return null
  const id = toNumberOrNull(raw.id)
  if (id === null) return null
  return raw.professional === true ? { id, name: toText(raw.name), professional: true } : { id, name: toText(raw.name) }
}

function normalizeOptions(raw: unknown): ContractHonorariumOptionDTO[] {
  return Array.isArray(raw)
    ? raw.map(normalizeOption).filter((option): option is ContractHonorariumOptionDTO => option !== null)
    : []
}

function normalizePlan(raw: unknown): ContractHonorariumPlanOptionDTO | null {
  if (!isRecord(raw)) return null
  const id = toNumberOrNull(raw.id)
  const healthEntityId = toNumberOrNull(raw.healthEntityId)
  if (id === null || healthEntityId === null) return null
  return { id, name: toText(raw.name), healthEntityId, healthEntityName: toText(raw.healthEntityName) }
}

function listOf<T>(raw: unknown, normalize: (item: unknown) => T | null): T[] {
  return Array.isArray(raw) ? raw.map(normalize).filter((item): item is T => item !== null) : []
}

function normalizeFilterValue(value: unknown): unknown {
  if (typeof value !== 'string') return value
  return value
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/%/g, '')
}

function normalizeFilterDTO(filterDTO: unknown): unknown {
  if (!isRecord(filterDTO) || !Array.isArray(filterDTO.filters)) return null
  return {
    ...filterDTO,
    filterLists: [],
    filters: filterDTO.filters.filter(isRecord).map((filter) => ({
      ...filter,
      values: Array.isArray(filter.values) ? filter.values.map(normalizeFilterValue) : filter.values,
    })),
  }
}

export async function getContractHonorariums(
  offSet: number,
  pageSize: number,
  params: ContractHonorariumListQueryParams,
): Promise<ContractHonorariumListItemDTO[]> {
  const response = await axiosInstance.post(`${BASE}/list`, {
    loadConfigDTO: {
      offSet,
      pageSize,
      sortingFieldInfoList: params.multiSortMeta,
      filters: normalizeFilterDTO(params.filterDTO),
    },
  })
  return listOf(response.data, normalizeListItem)
}

export async function getContractHonorarium(id: number): Promise<ContractHonorariumDetailDTO> {
  const response = await axiosInstance.get(`${BASE}/${id}`)
  const detail = normalizeDetail(response.data)
  if (!detail) throw new Error('Respuesta inválida al cargar la distribución')
  return detail
}

export async function getContractHonorariumHistory(id: number): Promise<ContractHonorariumHistoryItemDTO[]> {
  const response = await axiosInstance.get(`${BASE}/${id}/history`)
  return listOf(response.data, normalizeHistoryItem)
}

export async function getContractHonorariumOptions(): Promise<ContractHonorariumOptionsDTO> {
  const response = await axiosInstance.get(`${BASE}/options`)
  const data: unknown = response.data
  const record = isRecord(data) ? data : {}
  return {
    billingEntities: normalizeOptions(record.billingEntities),
    healthEntities: normalizeOptions(record.healthEntities),
    professionals: normalizeOptions(record.professionals),
    distributionTypes: normalizeOptions(record.distributionTypes),
    buildings: normalizeOptions(record.buildings),
  }
}

export async function getContractHonorariumPlans(healthEntityIds: number[]): Promise<ContractHonorariumPlanOptionDTO[]> {
  const response = await axiosInstance.get(`${BASE}/plans`, { params: { healthEntityIds: healthEntityIds.join(',') } })
  return listOf(response.data, normalizePlan)
}

export async function searchContractHonorariumNomenclators(filter: string): Promise<ContractHonorariumOptionDTO[]> {
  const response = await axiosInstance.get(`${BASE}/nomenclators`, { params: { filter } })
  return normalizeOptions(response.data)
}

export async function createContractHonorariums(
  payload: ContractHonorariumCreateDTO,
): Promise<ContractHonorariumCreateResultDTO> {
  const response = await axiosInstance.post(BASE, payload)
  const data: unknown = response.data
  const record = isRecord(data) ? data : {}
  return {
    created: toNumberOrNull(record.created) ?? 0,
    total: toNumberOrNull(record.total) ?? 0,
    duplicates: listOf(record.duplicates, normalizeCombination),
  }
}

export async function updateContractHonorarium(id: number, payload: ContractHonorariumUpdateDTO): Promise<boolean> {
  const response = await axiosInstance.put(`${BASE}/${id}`, payload)
  const data: unknown = response.data
  return isRecord(data) ? data.updated !== false : true
}

export async function deleteContractHonorarium(id: number): Promise<void> {
  await axiosInstance.delete(`${BASE}/${id}`)
}

export async function closeContractHonorarium(id: number, closeDate: string): Promise<void> {
  await axiosInstance.post(`${BASE}/${id}/close`, { closeDate })
}

const SKIP_REASONS: ContractHonorariumSkipReason[] = ['EXPIRED', 'ENDS_BEFORE_DATE', 'FUTURE', 'NO_CHANGES']

function normalizeBulkResult(raw: unknown): ContractHonorariumBulkResultDTO {
  const record = isRecord(raw) ? raw : {}
  const byReason = isRecord(record.skippedByReason) ? record.skippedByReason : {}
  const skippedByReason: ContractHonorariumBulkResultDTO['skippedByReason'] = {}
  for (const reason of SKIP_REASONS) {
    const count = toNumberOrNull(byReason[reason])
    if (count) skippedByReason[reason] = count
  }
  return {
    modified: toNumberOrNull(record.modified) ?? 0,
    skipped: toNumberOrNull(record.skipped) ?? 0,
    skippedByReason,
  }
}

export async function bulkEditContractHonorariums(payload: ContractHonorariumBulkEditDTO): Promise<ContractHonorariumBulkResultDTO> {
  const response = await axiosInstance.post(`${BASE}/bulk/edit`, payload)
  return normalizeBulkResult(response.data)
}

export async function bulkCloseContractHonorariums(payload: ContractHonorariumBulkCloseDTO): Promise<ContractHonorariumBulkResultDTO> {
  const response = await axiosInstance.post(`${BASE}/bulk/close`, payload)
  return normalizeBulkResult(response.data)
}
